import {
  type GetArtifactResp,
  type HeavyArtifactResponse,
  type OAuthConnectionArtifact,
  isEditableAfterSave,
} from "../../types/artifacts.ts";
import type { GetMessageResp } from "../../types/messages.ts";

import { isCancelledOrSuperseded } from "@/types/artifacts-shared.ts";

export const isBrowserSignInArtifact = (
  artifact: GetArtifactResp,
): artifact is OAuthConnectionArtifact & { purpose: "browserAuth" } =>
  artifact.artifactType === "oauthConnection" && artifact.purpose === "browserAuth";
/**
 * A sent text whose delivery is still unsettled: SendBlue accepted the send
 * but hasn't delivered it, and the backend may yet settle it either way
 * (deliveryState absent = legacy draft with no delivery tracking = settled).
 * The `status === "sent"` guard mirrors the server's label rule — delivery
 * state only means something on a draft that was actually sent.
 */
export const isTextDeliveryInFlight = (artifact: HeavyArtifactResponse): boolean =>
  artifact.artifactType === "textMessageDraft" &&
  artifact.status === "sent" &&
  artifact.deliveryState === "pending";

/**
 * A text whose delivery terminally failed ("Not delivered"). The reconciler
 * flips such a draft's status from "sent" to "failed" when it finalizes the
 * failure, so this must match the flipped shape too — `deliveryState` is the
 * discriminator that separates a real send that never arrived from a draft
 * the send API refused outright (no delivery tracking, plain "Failed").
 *
 * Must agree with the server's `mapTextMessageStatusLabel`
 * (api/src/context/artifacts.ts), which mints the "Not delivered" label from
 * the same condition — drift makes the chip and the date line disagree.
 */
export const isTextDeliveryFailed = (artifact: HeavyArtifactResponse): boolean =>
  artifact.artifactType === "textMessageDraft" &&
  (artifact.status === "sent" || artifact.status === "failed") &&
  artifact.deliveryState === "failed";

/**
 * "Final" statuses are the ones where the artifact has reached a terminal
 * state — no further user action is possible. Drives chip opacity styling
 * and the date prefix in `<ArtifactStatusDate>`. An in-flight text delivery
 * is not final even though its status is "sent"; a terminally failed one is
 * final even after its status flips to "failed".
 */
export const isArtifactFinalStatus = (artifact: HeavyArtifactResponse): boolean => {
  if (isTextDeliveryInFlight(artifact)) {
    return false;
  }
  return (
    artifact.status === "completed" ||
    isCancelledOrSuperseded(artifact.status) ||
    artifact.status === "sent" ||
    artifact.status === "connected" ||
    isTextDeliveryFailed(artifact)
  );
};

/**
 * Whether a heavy artifact's body renders read-only: editable while it is
 * waiting for the user to act on it, or after its save for the types whose
 * card *is* the saved object (a document — see `isEditableAfterSave`), and
 * never on a shared (read-only) thread. Shared by the in-thread lightbox, the
 * biscuit and the canvas draft surface so the rule can't drift between them —
 * and it mirrors the server's patch gate, so the card never offers an edit the
 * server would refuse.
 */
export const isArtifactReadOnly = (
  artifact: HeavyArtifactResponse,
  isSharedThread: boolean,
): boolean =>
  isSharedThread ||
  !(
    artifact.status === "waitingForUserAction" ||
    isEditableAfterSave(artifact.artifactType, artifact.status)
  );

/**
 * True for a live reply-stream placeholder — an `inProgress` message carrying an
 * `inProgress` `userResponse` artifact. `AgentMessageItem` otherwise renders
 * `inProgress` messages as `null`; this keeps the streaming reply visible while
 * its deltas arrive (the artifact's `status` is the single source of truth — the
 * streaming path is the only producer of an `inProgress` userResponse).
 */
export const isStreamingReplyPlaceholder = (message: GetMessageResp): boolean =>
  message.artifacts?.some((a) => a.artifactType === "userResponse" && a.status === "inProgress") ??
  false;

// Breadcrumb tests live in shared/client so the feed-relocation selectors can use
// the same predicates the renderers do. Re-exported here so existing call sites
// keep their import site.
export {
  artifactsHaveBreadcrumbs,
  messageHasRenderableArtifacts,
  messageIsBreadcrumb,
  messageIsTextOnlyBreadcrumb,
} from "@/shared/breadcrumbs.ts";
